import { ActionTree } from 'vuex';
import { WorldState } from './types';
import { RootState } from '../types';
import FirebaseGateway from '@/classes/firebaseGateway';
import { WorldMarker } from '@/interfaces/Marker';
import { Person } from '@/interfaces/Person';
import { World } from '@/interfaces/world/world';
import { LibraryItem } from '@/interfaces/world/library';

export const actions: ActionTree<WorldState, RootState> = {

  /**
   * @param param0
   * @param marker
   */
  addMarker({ commit, state }, marker: WorldMarker): Promise<string> {
    if (state.currentWorld === null) {
      throw new Error('CurrentWorld is empty');
    }

    const promise = new FirebaseGateway().saveMarker(state.currentWorld, marker);
    promise.then((markerId) => {
      // Set the id and save to state
      marker.id = markerId;
      commit('addMarker', marker);
    });
    return promise;
  },

  async loadPersons({ state, commit, dispatch, rootState }, groupId) {
    if (groupId === null) {
      throw new Error('No group selected');
    }
    if (rootState.user === null) {
      throw new Error('User is null');
    }

    const firebasePersons = await new FirebaseGateway().getPublicPersons(groupId);
    const firebasePrivatePersons = await new FirebaseGateway().getPrivatePersons(groupId, rootState.user.uid);

    const allPersons: Person[] = [];
    const processFirebaseResult = (querySnapshot: firebase.firestore.QuerySnapshot) => {
      querySnapshot.forEach((doc) => {
        // incorporate the id of the document into the object
        const person = doc.data() as Person;
        person.id = doc.id;

        // Fetch the image url if it hasn't been saved
        if (typeof person.image !== 'undefined' && person.image !== null
          && !person.loadedImageUrl) {
          dispatch('loadImageUrl', person);
        }
        commit('addPerson', person);
        allPersons.push(person);
      });
    };

    processFirebaseResult(firebasePersons);
    processFirebaseResult(firebasePrivatePersons);

    return Promise.resolve(allPersons);
  },

  async getPersonsByLocation({ state, dispatch, rootState }, locationId) {
    let personsOfLocation: Person[] = [];
    if (state.persons.length === 0) {
      personsOfLocation = await dispatch('loadPersons', rootState.currentGroup);
    } else {
      personsOfLocation = state.persons;
    }

    personsOfLocation = personsOfLocation.filter((person) => person.location === locationId);
    return Promise.resolve(personsOfLocation);
  },

  /**
   * Loads the map markers
   */
  loadWorldMapMarkers({ commit, state }) {
    if (state.markers.length > 0) {
      return Promise.resolve(true);
    }

    if (state.currentWorld === null) {
      throw new Error('loadWorldMapMarkers: CurrentWorld is null');
    }

    const loadWorldPromise = new FirebaseGateway().loadMapMarkers(state.currentWorld);
    loadWorldPromise.then((mapMarkers) => {

      mapMarkers.forEach((markerDocument) => {
        const marker = markerDocument.data();
        marker.id = markerDocument.id;
        commit('addMarker', marker);
      });
    });
    return loadWorldPromise;
  },

  loadWorlds({ commit, rootState }) {
    let userId: string | null = null;
    if (rootState.user !== null) {
      userId = rootState.user.uid;
    }
    const worldsPromise = new FirebaseGateway().getWorlds(userId);

    worldsPromise.then((worldSnapshots) => {
      worldSnapshots.forEach((worldSnapshot) => {
        worldSnapshot.docs.forEach((worldDoc) => {
          const worldData: World = worldDoc.data() as World;
          worldData.slug = worldDoc.id;
          commit('saveAvailableWorld', worldDoc);
        });
      });
    });

    return worldsPromise;
  },

  createLibraryItem({ commit, state }, libraryItem: LibraryItem) {
    if (state.currentWorld === null) {
      throw new Error('current world is null, cannot upload library item');
    }
    return FirebaseGateway.uploadLibraryItem(state.currentWorld, libraryItem).then((itemId) => {
      libraryItem.id = itemId;
      commit('setLibraryItem', libraryItem);
      return true;
    });
  },

  loadLibrary({ commit, state }) {
    if (state.currentWorld === null) {
      throw new Error('current world is null, cannot upload library item');
    }

    FirebaseGateway.loadLibrary(state.currentWorld).then((library) => {
      library.forEach((item) => {
        commit('setLibraryItem', item);
      });
    });
  },
};
